import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, FileText, Lock, BarChart3, ChevronRight, ExternalLink, Snowflake,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useEngagement } from '@/api/hooks/useCtsAudit';
import { ReadinessBar } from './AuditCenterPage';
import {
  notYetAssessable,
  type EvidenceRequest, type FrameworkNode, type FrameworkReadiness,
} from '@/api/types/cts-audit.types';

// ── TC5 — the engagement workspace ───────────────────────────────────────────────────────────
//
// ⚠ POINT-IN-TIME ONLY. No sparklines, no trend arrows, no "since last month". Nothing stores a
// historical readiness series, so any movement drawn here would be invented — the same ruling as
// TC4's BI trends.
//
// ⚠ AND THERE IS DELIBERATELY NO HEADLINE PERCENTAGE ON THIS PAGE. A single number cannot carry
// "23 of 29 were assessable" with it; the counts are shown instead, and the reader is told what
// was excluded and why. The landing rail's percentage is the conservative met/total, which is a
// different question asked at a glance.

const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.1em] text-text-secondary';

const fmt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';

const LEGEND: { key: keyof FrameworkReadiness; label: string; dot: string }[] = [
  { key: 'met',            label: 'Met',         dot: 'bg-cert-valid-fg' },
  { key: 'partially',      label: 'Partially',   dot: 'bg-cert-pending-fg' },
  { key: 'not_met',        label: 'Not met',     dot: 'bg-cert-expired-fg' },
  { key: 'not_started',    label: 'Not started', dot: 'bg-text-faint/45' },
  { key: 'unknown',        label: 'Unknown',     dot: 'bg-text-faint/35' },
  { key: 'unmapped',       label: 'Unmapped',    dot: 'bg-text-faint/28' },
  { key: 'out_of_scope',   label: 'Out of scope', dot: 'bg-text-faint/24' },
  { key: 'not_applicable', label: 'N/A',         dot: 'bg-text-faint/20' },
];

function FrameworkBlock({ r, nodes, e_control_count }: {
  r: FrameworkReadiness; nodes: FrameworkNode[]; e_control_count: number;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const gaps = notYetAssessable(r);
  const mine = nodes.filter((n) => n.framework_reference === r.framework_reference);

  return (
    <section className={cn(PANEL, 'mb-[16px] px-[18px] py-[16px]')}
      aria-label={`Readiness — ${r.name}`}>
      <div className="flex flex-wrap items-baseline gap-x-[10px] gap-y-[4px]">
        <h3 className="text-[16px] font-semibold text-text-primary">{r.name}</h3>
        <span className="rounded-[4px] bg-accent-cyan/12 px-[7px] py-[2px] font-mono text-[10px] uppercase tracking-[0.08em] text-accent-cyan">
          In scope
        </span>
        {/* ⚠ THE DENOMINATOR IS NAMED IN THE HEADLINE. "14 of 23 assessed" cannot be misread as
            "14 of everything" — which is exactly how a bare percentage gets misread. */}
        <span className="ml-auto text-[13px] text-text-secondary">
          <strong className="text-text-primary">{r.met} of {r.assessed}</strong> assessed requirements met
        </span>
      </div>

      <p className="mt-[4px] text-[12.5px] leading-[1.5] text-text-secondary">
        {r.assessed} of {r.total} requirements assessed · {gaps} currently not assessable.
        {/* ⚠ THE DENOMINATOR IS NAMED. This is readiness over the controls THIS auditor was
            granted — not over the whole framework — so it can differ from the programme figure on
            the Audit Center, and the reader is told which question each answers. */}
        {' '}Counted over the {e_control_count} controls in this engagement&apos;s granted scope;
        readiness reflects only assessable requirements, and no blended score is calculated.
      </p>

      <ReadinessBar r={r} className="mt-[12px] h-[9px] rounded-[4px]" />

      <div className="mt-[9px] flex flex-wrap gap-x-[16px] gap-y-[5px]">
        {LEGEND.filter((l) => (r[l.key] as number) > 0).map((l) => (
          <span key={l.key} className="flex items-center gap-[6px] text-[11.5px] text-text-secondary">
            <span className={cn('h-[8px] w-[8px] shrink-0 rounded-[2px]', l.dot)} />
            {l.label} <span className="tabular-nums text-text-primary">{r[l.key] as number}</span>
          </span>
        ))}
      </div>

      {gaps > 0 && (
        // ⚠ THE GAPS ARE NAMED AND EXPLAINED, in one strip, so "not yet assessable" cannot be read
        // as a softer way of saying "failed". An unmapped requirement is a platform content gap
        // the tenant cannot fix by working harder.
        <div className="mt-[12px] rounded-[8px] border border-border-subtle bg-white/[0.02] px-[13px] py-[10px]">
          <span className={cn(EYEBROW, 'mr-[10px] text-text-faint')}>Not yet assessable</span>
          <span className="text-[12.5px] text-text-secondary">
            {[
              r.not_started ? `${r.not_started} not started` : null,
              r.unknown ? `${r.unknown} unknown` : null,
              r.unmapped ? `${r.unmapped} unmapped` : null,
              r.out_of_scope ? `${r.out_of_scope} outside this scope` : null,
              r.not_applicable ? `${r.not_applicable} N/A` : null,
            ].filter(Boolean).join(' · ')}
            . Unmapped requirements are identified gaps to address, not failed requirements
            {r.out_of_scope > 0 && '; out-of-scope ones are mapped in the library but not granted to this auditor'}.
          </span>
        </div>
      )}

      {mine.length > 0 && (
        <>
          <p className="mt-[16px] text-[11px] text-text-faint">
            <span className={cn(EYEBROW, 'mr-[8px] text-text-secondary')}>
              {/* ⚠ THE FRAMEWORK'S OWN WORD, from its data — never "family", which is VeriGRC's
                  control taxonomy and a different hierarchy entirely. */}
              {r.hierarchy_label ?? 'Structure'}
            </span>
            the framework&apos;s own hierarchy · expand a row to see its requirements
          </p>

          <ul className="mt-[8px]">
            {mine.map((n) => {
              const isOpen = open === n.node_code;
              const notes = [
                n.unmapped_codes.length
                  ? `${n.unmapped_codes.join(', ')} ${n.unmapped_codes.length === 1 ? 'has' : 'have'} no mapped control. Map a control to make ${n.unmapped_codes.length === 1 ? 'this requirement' : 'these requirements'} assessable.`
                  : null,
                n.out_of_scope_codes.length
                  ? `${n.out_of_scope_codes.join(', ')} ${n.out_of_scope_codes.length === 1 ? 'is' : 'are'} mapped in the library but not granted to this auditor.`
                  : null,
                n.na_codes.length
                  ? `${n.na_codes.length} requirement${n.na_codes.length === 1 ? '' : 's'} excluded as N/A.`
                  : null,
              ].filter(Boolean) as string[];

              return (
                <li key={n.node_code} className="border-t border-border-subtle first:border-t-0">
                  <button type="button" onClick={() => setOpen(isOpen ? null : n.node_code)}
                    aria-expanded={isOpen}
                    className="grid w-full grid-cols-[16px_46px_minmax(0,1fr)_110px_minmax(0,1.1fr)] items-center gap-[10px] py-[10px] text-left hover:bg-white/[0.02]">
                    <ChevronRight className={cn('h-[13px] w-[13px] text-text-faint transition-transform',
                      isOpen && 'rotate-90')} aria-hidden="true" />
                    <span className="font-mono text-[11px] text-text-faint">{n.node_code}</span>
                    <span className="truncate text-[13px] text-text-primary">{n.node_name}</span>
                    <span className="text-[12.5px] text-text-secondary">
                      {/* ⚠ A node nobody has touched says "Not started" rather than "0 of 4 met" —
                          a zero here would read as four failed requirements. */}
                      {n.assessable === 0 ? 'Not started' : `${n.met} of ${n.assessable} met`}
                    </span>
                    <span className="flex h-[6px] overflow-hidden rounded-[3px] bg-bg-surface-2">
                      {n.assessable > 0 && (
                        <>
                          <span className="block h-full bg-cert-valid-fg"
                            style={{ width: `${(n.met / n.total) * 100}%` }} />
                          <span className="block h-full bg-cert-pending-fg/70"
                            style={{ width: `${((n.assessable - n.met) / n.total) * 100}%` }} />
                        </>
                      )}
                    </span>
                  </button>

                  {isOpen && notes.length > 0 && (
                    <p className="pb-[11px] pl-[72px] text-[11.5px] leading-[1.5] text-cert-pending-fg">
                      {notes.join(' ')}
                    </p>
                  )}
                  {isOpen && notes.length === 0 && (
                    <p className="pb-[11px] pl-[72px] text-[11.5px] text-text-faint">
                      Every requirement here is mapped and assessable.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

const REQ_TONE: Record<EvidenceRequest['status'], { label: string; dot: string; text: string }> = {
  open:      { label: 'Open',      dot: 'bg-accent-cyan',      text: 'text-accent-cyan' },
  fulfilled: { label: 'Fulfilled', dot: 'bg-cert-valid-fg',    text: 'text-cert-valid-fg' },
  withdrawn: { label: 'Withdrawn', dot: 'bg-text-faint',       text: 'text-text-faint' },
};

function RequestCard({ r }: { r: EvidenceRequest }) {
  const t = REQ_TONE[r.status];
  return (
    <li className="border-t border-border-subtle py-[11px] first:border-t-0 first:pt-0">
      <p className="text-[12.5px] leading-[1.45] text-text-primary">{r.title}</p>
      <div className="mt-[5px] flex flex-wrap items-center gap-x-[10px] gap-y-[4px]">
        <Link to={`/compliance/workspace/controls/${r.control_reference}`}
          className="font-mono text-[11px] text-accent-cyan hover:underline">
          {r.control_code ?? r.control_reference}
        </Link>
        <span className={cn('ml-auto flex items-center gap-[5px] rounded-full bg-white/[0.05] px-[8px] py-[2px] text-[11px]', t.text)}>
          <span className={cn('h-[5px] w-[5px] rounded-full', t.dot)} />{t.label}
        </span>
      </div>
      {/* ⚠ Overdue and "no due date" never look alike — a missing deadline is not a missed one. */}
      <p className={cn('mt-[4px] text-[11px]',
        r.due_state === 'overdue' ? 'font-medium text-cert-expired-fg' : 'text-text-faint')}>
        {r.status === 'fulfilled' ? `Fulfilled ${fmt(r.fulfilled_at)}`
          : r.status === 'withdrawn' ? `Withdrawn ${fmt(r.withdrawn_at)}`
          : r.due_state === 'overdue' ? `Overdue · ${fmt(r.due_at)}`
          : r.due_at ? `Due ${fmt(r.due_at)}` : 'No due date'}
      </p>
    </li>
  );
}

export function EngagementDetailPage() {
  const { tokenId } = useParams<{ tokenId: string }>();
  const { data, isLoading, isError } = useEngagement(tokenId);
  const [tab, setTab] = useState<'readiness' | 'requests'>('readiness');

  const counts = useMemo(() => {
    const rs = data?.requests ?? [];
    return {
      open: rs.filter((r) => r.status === 'open').length,
      overdue: rs.filter((r) => r.due_state === 'overdue').length,
      fulfilled: rs.filter((r) => r.status === 'fulfilled').length,
    };
  }, [data]);

  if (isLoading) {
    return <div className="space-y-[14px] p-[24px]"><Skeleton className="h-[120px] w-full" /><Skeleton className="h-[300px] w-full" /></div>;
  }
  if (isError || !data) {
    return (
      <div className="p-[24px]">
        <Link to="/compliance/workspace/audit" className="text-[13px] text-accent-cyan hover:underline">
          ← Back to the Audit Center
        </Link>
        <p className="mt-[12px] text-[13px] text-cert-expired-fg">That engagement could not be found.</p>
      </div>
    );
  }

  const { engagement: e, readiness, nodes, requests, controls } = data;
  const live = e.window_state === 'active';

  return (
    <div className="p-[24px]">
      <div className="mb-[16px] flex flex-wrap items-start gap-[16px]">
        <div className="min-w-0 flex-1">
          <Link to="/compliance/workspace/audit"
            className="mb-[8px] inline-flex items-center gap-[6px] text-[12.5px] text-text-secondary hover:text-text-primary">
            <ArrowLeft className="h-[14px] w-[14px]" aria-hidden="true" /> Audit Center
          </Link>
          <h1 className="text-[25px] font-bold text-text-primary">Audit Center</h1>
          <p className="mt-[4px] text-[13px] text-text-secondary">
            Manage audit engagements, evidence requests, fulfillment, and audit readiness in one place.
          </p>
        </div>
        <Link to="/settings/auditor-access"
          className={cn(PANEL, 'flex shrink-0 items-center gap-[7px] px-[13px] py-[9px] text-[13px] text-text-primary transition-colors hover:border-accent-cyan')}>
          Manage auditor access
          <ExternalLink className="h-[13px] w-[13px] text-text-faint" aria-hidden="true" />
        </Link>
      </div>

      <section className={cn(PANEL, 'mb-[16px] overflow-hidden')} aria-label="Engagement">
        <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[6px] px-[18px] pt-[15px]">
          <span className={cn('flex items-center gap-[6px] rounded-full px-[9px] py-[3px] text-[11.5px] font-medium',
            live ? 'bg-cert-valid-bg/40 text-cert-valid-fg' : 'bg-white/[0.05] text-text-secondary')}>
            <span className={cn('h-[6px] w-[6px] rounded-full', live ? 'bg-cert-valid-fg' : 'bg-text-faint')} />
            {live ? 'Live' : e.window_state === 'revoked' ? 'Revoked'
              : e.window_state === 'expired' ? 'Expired' : 'Invited'}
          </span>
          <h2 className="text-[16px] font-semibold text-text-primary">{e.audit_firm_name}</h2>
          <span className="text-[12.5px] text-text-secondary">Lead auditor · {e.auditor_display_name}</span>
        </div>

        <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[5px] px-[18px] pb-[13px] pt-[9px]">
          <span className={cn(EYEBROW, 'text-text-faint')}>In scope</span>
          {e.provenance.length === 0
            ? <span className="text-[12px] italic text-text-faint">Nothing granted yet</span>
            : e.provenance.map((p, i) => (
              <span key={p.source_framework_reference ?? i}
                className="rounded-[4px] bg-bg-surface-2 px-[8px] py-[2px] font-mono text-[10.5px] text-text-secondary">
                {p.name ?? 'Added directly'}
              </span>
            ))}
          <span className="text-[12px] text-text-secondary">· access closes {fmt(e.expires_at)}</span>
          <span className="text-[12px] text-text-faint">· {e.control_count} controls</span>
        </div>

        {/*
          ⚠ THE LOCK IS THE MODEL, STATED. The scope is a frozen control set resolved at grant, so
          it genuinely cannot be edited from a reading surface — and saying only "cannot be changed
          here" would invite someone to look for the edit button. The date says when it froze.
        */}
        <p className="flex flex-wrap items-center gap-[8px] border-t border-border-subtle bg-white/[0.02] px-[18px] py-[10px] text-[12px] text-text-secondary">
          <Lock className="h-[13px] w-[13px] shrink-0 text-text-faint" aria-hidden="true" />
          Audit scope is defined by the auditor&apos;s access permissions and cannot be changed from
          this view.
          {e.resolved_at && (
            <span className="flex items-center gap-[6px] text-text-faint">
              <Snowflake className="h-[12px] w-[12px] shrink-0" aria-hidden="true" />
              Frozen {fmt(e.resolved_at)} — library changes do not move it.
            </span>
          )}
        </p>
      </section>

      <div role="tablist" aria-label="Engagement views" className="mb-[16px] flex gap-[6px]">
        {([['readiness', 'Readiness', ShieldCheck], ['requests', 'Evidence requests', FileText]] as const)
          .map(([id, label, Icon]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
              className={cn('flex items-center gap-[7px] rounded-t-[9px] border-b-2 px-[14px] py-[9px] text-[13px] transition-colors',
                tab === id
                  ? 'border-accent-cyan bg-bg-surface font-medium text-text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary')}>
              <Icon className="h-[14px] w-[14px]" aria-hidden="true" />
              {label}
              {id === 'requests' && requests.length > 0 && (
                <span className="rounded-full bg-bg-surface-2 px-[6px] py-[1px] text-[10.5px] tabular-nums text-text-secondary">
                  {requests.length}
                </span>
              )}
            </button>
          ))}
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_320px] gap-[16px] max-[1100px]:grid-cols-1">
        <div className="min-w-0">
          {tab === 'readiness' ? (
            <>
              {/*
                ⚠ THE BANNER IS THE CONTRACT FOR EVERY NUMBER BELOW IT. Each sentence corresponds to
                a decision the model actually makes — derived not entered, unmapped explained not
                failed, never-assessed shown as not started, and no percentage rather than a
                misleading zero. Copy that promised more than the model does would be worse than no
                copy at all.
              */}
              <p className="mb-[14px] flex items-start gap-[9px] rounded-[9px] border border-border-subtle bg-white/[0.02] px-[14px] py-[11px] text-[12.5px] leading-[1.55] text-text-secondary">
                <BarChart3 className="mt-[2px] h-[14px] w-[14px] shrink-0 text-accent-cyan" aria-hidden="true" />
                <span>
                  <strong className="text-text-primary">Readiness is derived from actual control
                  work, not manually entered.</strong> Unmapped requirements are shown with a reason
                  and are not treated as failures. Never-assessed requirements appear as Not
                  started. When no requirements are scorable, no percentage is shown rather than
                  displaying a misleading zero.
                </span>
              </p>

              {readiness.length === 0 ? (
                <div className={cn(PANEL, 'px-[18px] py-[20px]')}>
                  <p className="text-[13px] text-text-secondary">
                    {e.control_count === 0
                      ? 'No scope has been granted, so there is nothing to assess yet.'
                      // ⚠ A scope built from individually-picked controls has no framework to
                      // report against. Real state, and the reason is said rather than left blank.
                      : 'This scope was built from individually selected controls, so there is no framework readiness to show.'}
                  </p>
                </div>
              ) : readiness.map((r) => (
                <FrameworkBlock key={r.framework_reference} r={r} nodes={nodes}
                  e_control_count={e.control_count} />
              ))}
            </>
          ) : (
            <div className={cn(PANEL, 'overflow-hidden')}>
              <div className="grid grid-cols-[minmax(0,1fr)_140px_150px] gap-[14px] bg-white/[0.035] px-[16px] py-[10px]">
                {['Request / Control', 'Status', 'Due'].map((h) => (
                  <span key={h} className={EYEBROW}>{h}</span>
                ))}
              </div>
              {requests.length === 0 ? (
                <p className="px-[16px] py-[20px] text-[13px] text-text-secondary">
                  Nothing has been requested for this engagement yet.
                </p>
              ) : requests.map((r) => (
                <div key={r.id}
                  className="grid grid-cols-[minmax(0,1fr)_140px_150px] items-start gap-[14px] border-t border-border-subtle px-[16px] py-[12px]">
                  <div className="min-w-0">
                    <span className="block text-[13px] text-text-primary">{r.title}</span>
                    <Link to={`/compliance/workspace/controls/${r.control_reference}`}
                      className="mt-[2px] inline-block font-mono text-[11.5px] text-accent-cyan hover:underline">
                      {r.control_code ?? r.control_reference}
                    </Link>
                    {r.status === 'fulfilled' && (
                      <p className="mt-[5px] text-[11.5px] text-cert-valid-fg">
                        Satisfied by {r.satisfied_by_title ?? 'evidence'}
                      </p>
                    )}
                    {r.status === 'withdrawn' && r.withdrawn_reason && (
                      <p className="mt-[5px] text-[11.5px] text-text-secondary">
                        <span className={cn(EYEBROW, 'mr-[7px]')}>Withdrawn</span>{r.withdrawn_reason}
                      </p>
                    )}
                  </div>
                  <span className={cn('flex w-fit items-center gap-[5px] rounded-full bg-white/[0.05] px-[8px] py-[2px] text-[11.5px]',
                    REQ_TONE[r.status].text)}>
                    <span className={cn('h-[5px] w-[5px] rounded-full', REQ_TONE[r.status].dot)} />
                    {REQ_TONE[r.status].label}
                  </span>
                  <span className={cn('text-[12px]',
                    r.due_state === 'overdue' ? 'font-medium text-cert-expired-fg'
                      : r.due_state === 'none' ? 'italic text-text-faint' : 'text-text-secondary')}>
                    {r.due_state === 'overdue' ? `Overdue · ${fmt(r.due_at)}`
                      : r.due_state === 'none' ? 'No due date'
                      : r.due_state === 'closed' ? '—' : `Due ${fmt(r.due_at)}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className={cn(PANEL, 'h-fit px-[15px] py-[14px]')} aria-label="Evidence requests">
          <div className="flex items-baseline justify-between gap-[8px]">
            <h2 className="text-[13px] font-semibold text-text-primary">Evidence requests</h2>
            <button type="button" onClick={() => setTab('requests')}
              className="text-[12px] font-medium text-accent-cyan hover:underline">
              View all ›
            </button>
          </div>

          <div className="mt-[12px] grid grid-cols-3 gap-[8px]">
            {([['open', 'Open', 'text-accent-cyan', 'bg-accent-cyan'],
               ['overdue', 'Overdue', 'text-cert-expired-fg', 'bg-cert-expired-fg'],
               ['fulfilled', 'Fulfilled', 'text-cert-valid-fg', 'bg-cert-valid-fg']] as const)
              .map(([k, label, text, dot]) => (
                <div key={k} className="rounded-[8px] border border-border-subtle px-[10px] py-[8px]">
                  <span className={cn('block text-[17px] font-bold tabular-nums',
                    counts[k] === 0 ? 'text-text-faint' : text)}>{counts[k]}</span>
                  <span className="mt-[1px] flex items-center gap-[5px] text-[11px] text-text-secondary">
                    <span className={cn('h-[5px] w-[5px] rounded-full',
                      counts[k] === 0 ? 'bg-text-faint/40' : dot)} />{label}
                  </span>
                </div>
              ))}
          </div>

          {requests.length === 0 ? (
            <p className="mt-[14px] text-[12.5px] text-text-secondary">Nothing requested yet.</p>
          ) : (
            <ul className="mt-[14px]">
              {requests.slice(0, 6).map((r) => <RequestCard key={r.id} r={r} />)}
            </ul>
          )}

          {controls.length > 0 && (
            <p className="mt-[14px] border-t border-border-subtle pt-[11px] text-[11.5px] text-text-faint">
              {controls.length} control{controls.length === 1 ? '' : 's'} in this engagement&apos;s
              frozen scope.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
